import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from './auth';
import { uploadPhotos, getUserPhotos, deletePhoto, searchUsers, checkIsFollowing, followUser, unfollowUser, blockUser, unblockUser, checkIsBlocked, reportPhoto } from './photoFunctions';
import { UserProfile, UserProfileButton } from './components/UserProfile';
import { Gallery, styles, ReportUserModal, BlockUserModal, showToast } from './components';
import { supabase } from './supabase';
import SkeletonGallery from './components/SkeletonGallery';
import ErrorState from './components/ErrorState';
import { useNetworkStatus, isNetworkError, getErrorMessage } from './utils/networkDetection';
import { UserSearchModal } from './components/UserSearchModal';
import { BottomNavBar } from './components/BottomNavBar';
import { CaptionModal } from './components/CaptionModal';

// Profile Page
export const ProfilePage = ({ onNavigateToHome, onNavigateToSettings, initialUsername, onProfileLoaded, previousPage, onBack }) => {
  const { user, profile, signOut } = useAuth();
  const [photos, setPhotos] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadStatus, setUploadStatus] = useState('');
  const [searchedUser, setSearchedUser] = useState(null);
  const [searchedPhotos, setSearchedPhotos] = useState([]);
  const [showSearchModal, setShowSearchModal] = useState(false);
  const [showUserProfile, setShowUserProfile] = useState(false);
  const [isFollowing, setIsFollowing] = useState(false);
  const [followLoading, setFollowLoading] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const [showReportModal, setShowReportModal] = useState(false);
  const [showBlockModal, setShowBlockModal] = useState(false);
  const [isBlocked, setIsBlocked] = useState(false);
  const [blockCheckLoading, setBlockCheckLoading] = useState(false);
  const [isBlockedByThem, setIsBlockedByThem] = useState(false);
  const [error, setError] = useState(null);
  const [loadingPhotos, setLoadingPhotos] = useState(true);
  const [selectedFiles, setSelectedFiles] = useState(null);
  const [showCaptionModal, setShowCaptionModal] = useState(false);
  const isOnline = useNetworkStatus();
  const [profileHistory, setProfileHistory] = useState([]); // Stack to track profile navigation chain
  const [loadingInitialUser, setLoadingInitialUser] = useState(false); // Track when loading initial user from navigation

  useEffect(() => {
    if (user) {
      loadUserPhotos();
    }
  }, [user]);

  // Handle initial username from navigation
  useEffect(() => {
    if (initialUsername && user) {
      // If we're already viewing a different user's profile, clear it first
      const currentUsername = searchedUser?.username;
      const isDifferentUser = currentUsername && currentUsername !== initialUsername;
      
      // Only load if we don't have a searchedUser, or if the username has changed
      if (!searchedUser || isDifferentUser) {
        // Clear previous searched user synchronously if navigating to a different user
        // This prevents showing the previous user's profile while loading the new one
        if (isDifferentUser) {
          setSearchedUser(null);
          setSearchedPhotos([]);
        }
        
        // Search for the user and load their profile
        const loadUserByUsername = async () => {
          try {
            setLoadingInitialUser(true); // Set loading state to prevent showing own profile
            // Clear history when navigating from external source (HomeFeed)
            setProfileHistory([]);
            const users = await searchUsers(initialUsername, user?.id);
            
            if (users.length > 0) {
              const foundUser = users[0];
              
              // Prevent selecting your own profile
              if (foundUser.id === user?.id || String(foundUser.id) === String(user?.id)) {
                // Just show own profile
                setLoadingInitialUser(false);
                if (onProfileLoaded) onProfileLoaded();
                return;
              }
              
              // Load user profile and photos
              setError(null);
              setSearchedUser(foundUser);
              const userPhotos = await getUserPhotos(foundUser.id, user?.id);
              setSearchedPhotos(userPhotos);
              
              // Notify that profile is loaded
              if (onProfileLoaded) onProfileLoaded();
            } else {
              // User not found, show own profile
              if (onProfileLoaded) onProfileLoaded();
            }
          } catch (error) {
            console.error('Error loading user profile from username:', error);
            setError(error);
            if (isNetworkError(error)) {
              showToast("Can't connect. Check your connection.");
            } else {
              showToast("Failed to load profile");
            }
            if (onProfileLoaded) onProfileLoaded();
          } finally {
            setLoadingInitialUser(false); // Clear loading state
          }
        };
        
        loadUserByUsername();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialUsername, user]);

  // Clear error when network comes back online
  useEffect(() => {
    if (isOnline && error) {
      setError(null);
      // Optionally auto-retry when coming back online
      if (user && photos.length === 0 && !searchedUser) {
        loadUserPhotos();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOnline]);

  useEffect(() => {
    if (showUserProfile) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [showUserProfile]);

  // No need to fetch blocked user IDs in frontend - backend handles all filtering

  const loadUserPhotos = async () => {
    if (!user?.id) return;
    setLoadingPhotos(true);
    try {
      setError(null); // Clear any previous errors
      // Pass currentUserId to check blocks (though loading own photos, so block check will be skipped)
      const userPhotos = await getUserPhotos(user.id, user.id);
      setPhotos(userPhotos);
    } catch (error) {
      console.error('Error loading photos:', error);
      setError(error);
      // Only show toast for non-network errors (network errors show in ErrorState)
      if (!isNetworkError(error)) {
        showToast(getErrorMessage(error));
      }
    } finally {
      setLoadingPhotos(false);
    }
  };

  const handleUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (!files || files.length === 0) return;

    // Store files and show caption modal
    setSelectedFiles(files);
    setShowCaptionModal(true);
    e.target.value = ''; // Reset input
  };

  const handleCaptionConfirm = async (captions) => {
    setShowCaptionModal(false);
    setUploading(true);
    
    try {
      await uploadPhotos(user.id, selectedFiles, captions);
      await loadUserPhotos();
    } catch (err) {
      console.error('Upload failed:', err);
      const errorMessage = err.message || err.error?.message || 'Unknown error occurred';
      
      // Handle different error types
      if (errorMessage.includes('inappropriate content')) {
        // NSFW content blocked
        alert(errorMessage);
      } else if (errorMessage.includes('Unable to check content safety') || errorMessage.includes('MODERATION_FAILED')) {
        // Moderation service error (not NSFW)
        alert('Unable to verify content safety at this time. Please try again in a few moments.');
      } else {
        // Other upload errors
        alert(`Upload failed: ${errorMessage}\n\nCommon issues:\n- Storage bucket "photos" may not exist\n- Storage policies may not be configured\n- Check browser console for details`);
      }
    } finally {
      setUploading(false);
      setSelectedFiles(null);
    }
  };

  const handleCaptionCancel = () => {
    setShowCaptionModal(false);
    setSelectedFiles(null);
  };

  const handleDelete = async (photoId, photoUrl) => {
    try {
      await deletePhoto(photoId, photoUrl);
      await loadUserPhotos();
    } catch (error) {
      console.error('Delete failed:', error);
      alert('Delete failed. Please try again.');
    }
  };

  const handleUserSelect = async (selectedUser) => {
    if (!user || !selectedUser) return;

    // Load profile with block checking
    try {
      setError(null);
      
      // If we're already viewing another user's profile, save it to history
      if (searchedUser) {
        setProfileHistory(prev => [...prev, {
          user: searchedUser,
          photos: searchedPhotos,
          isFollowing: isFollowing,
          isBlocked: isBlocked,
          isBlockedByThem: isBlockedByThem
        }]);
      }
      
      setSearchedUser(selectedUser);
      const userPhotos = await getUserPhotos(selectedUser.id, user?.id);
      setSearchedPhotos(userPhotos);
    } catch (error) {
      console.error('Error loading user profile:', error);
      setError(error);
      if (isNetworkError(error)) {
        showToast("Can't connect. Check your connection.");
      } else {
        showToast("Failed to load profile");
      }
    }
  };

  const checkFollowStatus = useCallback(async () => {
    if (!searchedUser || !user) return;
    
    try {
      const following = await checkIsFollowing(user.id, searchedUser.id);
      setIsFollowing(following);
    } catch (error) {
      console.error('Error checking follow status:', error);
    }
  }, [searchedUser, user]);

  useEffect(() => {
    // Check follow status when viewing someone else's profile
    if (searchedUser && user) {
      checkFollowStatus();
      checkBlockStatus();
    } else {
      setIsFollowing(false);
      setIsBlocked(false);
      setIsBlockedByThem(false);
    }
  }, [searchedUser, user, checkFollowStatus]);

  const checkBlockStatus = useCallback(async () => {
    if (!searchedUser || !user) return;
    
    setBlockCheckLoading(true);
    try {
      // Check block relationship in both directions
      const { data: blockData, error } = await supabase
        .from('blocked_users')
        .select('blocker_id, blocked_id')
        .or(`and(blocker_id.eq.${user.id},blocked_id.eq.${searchedUser.id}),and(blocker_id.eq.${searchedUser.id},blocked_id.eq.${user.id})`)
        .maybeSingle();
      
      if (error) throw error;
      
      if (blockData) {
        const theyBlockedUs = blockData.blocker_id === searchedUser.id;
        const weBlockedThem = blockData.blocker_id === user.id;
        
        setIsBlocked(weBlockedThem);
        setIsBlockedByThem(theyBlockedUs);
        
        // If they blocked us, clear the profile view immediately
        if (theyBlockedUs) {
          clearSearch();
          showToast("This account is unavailable");
        }
      } else {
        setIsBlocked(false);
        setIsBlockedByThem(false);
      }
    } catch (error) {
      console.error('Error checking block status:', error);
    } finally {
      setBlockCheckLoading(false);
    }
  }, [searchedUser, user]);

  const handleFollowToggle = async () => {
    if (!searchedUser || !user || followLoading) return;
    
    setFollowLoading(true);
    try {
      if (isFollowing) {
        await unfollowUser(user.id, searchedUser.id);
        setIsFollowing(false);
      } else {
        await followUser(user.id, searchedUser.id);
        setIsFollowing(true);
      }
    } catch (error) {
      console.error('Follow/unfollow failed:', error);
      alert('Operation failed. Please try again.');
    } finally {
      setFollowLoading(false);
    }
  };

  const clearSearch = () => {
    setSearchedUser(null);
    setSearchedPhotos([]);
    setShowMenu(false);
    setProfileHistory([]); // Clear history when clearing search
  };

  const handleBack = async () => {
    // If there's history, restore the previous profile
    if (profileHistory.length > 0) {
      const previousProfile = profileHistory[profileHistory.length - 1];
      const newHistory = profileHistory.slice(0, -1);
      
      try {
        setError(null);
        setSearchedUser(previousProfile.user);
        setSearchedPhotos(previousProfile.photos);
        // Restore follow/block status from history
        if (previousProfile.isFollowing !== undefined) {
          setIsFollowing(previousProfile.isFollowing);
        }
        if (previousProfile.isBlocked !== undefined) {
          setIsBlocked(previousProfile.isBlocked);
        }
        if (previousProfile.isBlockedByThem !== undefined) {
          setIsBlockedByThem(previousProfile.isBlockedByThem);
        }
        setProfileHistory(newHistory);
        // Note: checkFollowStatus and checkBlockStatus will run automatically via useEffect
      } catch (error) {
        console.error('Error restoring previous profile:', error);
        // Fall back to clearing search
        clearSearch();
        if (onBack) {
          onBack();
        }
      }
    } else {
      // No history, use the original back behavior
      clearSearch();
      if (onBack) {
        onBack();
      }
    }
  };

  const handleReportUser = async (photoId, reporterId, reportedUserId, reason) => {
    try {
      // For user reports, photoId should always be null
      // We're reporting the user, not a specific photo
      await reportPhoto('user', reporterId, reportedUserId, reason, null);
      showToast("Thanks for reporting. We'll review this soon.");
      setShowReportModal(false);
    } catch (error) {
      console.error('Report failed:', error);
      throw error;
    }
  };

  const handleBlockUser = async (blockerId, blockedId) => {
    try {
      // blockUser function now handles automatic unfollowing in the database
      await blockUser(blockerId, blockedId);
      setIsBlocked(true);
      setIsFollowing(false); // Update UI state
      showToast(`Blocked @${searchedUser?.username || 'user'}`);
      setShowBlockModal(false);
      
      // Redirect back to own profile after a short delay
      setTimeout(() => {
        clearSearch();
      }, 1000);
    } catch (error) {
      console.error('Block failed:', error);
      throw error;
    }
  };

  const handleUnblockUser = async (blockerId, blockedId) => {
    try {
      await unblockUser(blockerId, blockedId);
      setIsBlocked(false);
      showToast(`Unblocked @${searchedUser?.username || 'user'}`);
      setShowBlockModal(false);
    } catch (error) {
      console.error('Unblock failed:', error);
      throw error;
    }
  };

  const buttonStyle = {
    padding: 'clamp(8px, 2vw, 10px) clamp(12px, 3vw, 24px)',
    minHeight: '44px',
    minWidth: '44px',
    background: '#312310',
    color: '#e6c57e',
    border: 'none',
    fontFamily: "'Montserrat', sans-serif",
    fontSize: 'clamp(10px, 2.5vw, 12px)',
    letterSpacing: '0.1em',
    textTransform: 'uppercase',
    cursor: 'pointer',
    transition: 'all 0.4s ease',
    fontWeight: 300,
    WebkitTapHighlightColor: 'transparent',
    borderRadius: '4px'
  };

  // Don't show own profile while loading initial user from navigation
  const currentUser = loadingInitialUser ? null : (searchedUser || profile);
  // Don't show photos if the user is blocked by them, or if loading initial user
  const currentPhotos = loadingInitialUser ? [] : ((searchedUser && isBlockedByThem) ? [] : (searchedUser ? searchedPhotos : photos));
  const canDelete = !searchedUser; // Can only delete own photos

  return (
      <div style={{ 
      minHeight: '100vh', 
      background: '#a2a9ac',
      paddingTop: 'env(safe-area-inset-top)',
      paddingBottom: 'env(safe-area-inset-bottom)',
      paddingLeft: 'env(safe-area-inset-left)',
      paddingRight: 'env(safe-area-inset-right)'
    }}>
      <style>{styles}</style>
      <style>{`
        @media (max-width: 480px) {
          .nav-buttons-right {
            flex-direction: column !important;
          }
        }
      `}</style>
      
      {/* Search Modal */}
      <UserSearchModal 
        isOpen={showSearchModal}
        onClose={() => setShowSearchModal(false)}
        onSelectUser={handleUserSelect}
        currentUserId={user?.id}
        excludeUserId={user?.id}
      />

      {/* Caption Modal */}
      {showCaptionModal && selectedFiles && (
        <CaptionModal
          files={selectedFiles}
          onConfirm={handleCaptionConfirm}
          onCancel={handleCaptionCancel}
        />
      )}
      
      {/* Header */}
      <header style={{
        textAlign: 'center',
        padding: 'clamp(40px, 8vw, 60px) clamp(20px, 5vw, 40px) clamp(20px, 5vw, 30px)',
        background: 'linear-gradient(180deg, #a2a9ac 0%, rgba(162,169,172,0) 100%)',
        position: 'relative'
      }}>
        {/* Back Button - Only show when viewing another user's profile */}
        {searchedUser && (
          <button
            onClick={handleBack}
            style={{
              position: 'absolute',
              top: 'calc(30px + env(safe-area-inset-top))',
              left: 'calc(30px + env(safe-area-inset-left))',
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: 'clamp(8px, 2vw, 12px)',
              minWidth: '44px',
              minHeight: '44px',
              transition: 'all 0.3s ease',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              opacity: 0.8,
              borderRadius: '8px',
              WebkitTapHighlightColor: 'transparent',
              zIndex: 1000
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.opacity = '1';
              e.currentTarget.style.transform = 'scale(1.1)';
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.opacity = '0.8';
              e.currentTarget.style.transform = 'scale(1)';
            }}
            title="Back"
          >
            <svg 
              width="clamp(24px, 6vw, 32px)" 
              height="clamp(24px, 6vw, 32px)" 
              viewBox="0 0 24 24" 
              fill="none" 
              stroke="#312310" 
              strokeWidth="2" 
              strokeLinecap="round" 
              strokeLinejoin="round"
              style={{
                filter: 'drop-shadow(0 1px 2px rgba(49,35,16,0.3))'
              }}
            >
              <path d="M19 12H5M12 19l-7-7 7-7"/>
            </svg>
          </button>
        )}

        {/* Profile Picture & Username Container */}
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 'clamp(8px, 2vw, 16px)'
        }}>
          {/* Profile Picture */}
          <div style={{
            width: 'clamp(80px, 20vw, 120px)',
            height: 'clamp(80px, 20vw, 120px)',
            borderRadius: '50%',
            background: loadingInitialUser ? '#c6a25b' : (currentUser?.avatar 
              ? `url(${currentUser.avatar}) center/cover` 
              : '#c6a25b'),
            border: '4px solid #af731f',
            boxShadow: '0 4px 15px rgba(49,35,16,0.2)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 'clamp(2em, 8vw, 4em)',
            color: '#312310',
            fontFamily: "'Apple Garamond', Garamond, 'Times New Roman', serif",
            fontWeight: 400,
            flexShrink: 0
          }}>
            {loadingInitialUser ? '…' : (!currentUser?.avatar && (currentUser?.username?.[0] || 'U').toUpperCase())}
          </div>

          {/* Username */}
          <h1 style={{
            fontFamily: "'Apple Garamond', Garamond, 'Times New Roman', serif",
            fontSize: 'clamp(2em, 8vw, 5em)',
            color: '#312310',
            letterSpacing: '0.08em',
            fontWeight: 400,
            marginBottom: '0',
            wordBreak: 'break-word',
            overflowWrap: 'break-word',
            maxWidth: '100%',
            padding: '0 20px',
            boxSizing: 'border-box'
          }}>
            {loadingInitialUser ? 'Loading...' : (currentUser?.username || 'Loading...')}
          </h1>
        </div>

        <p style={{
          fontFamily: "'Montserrat', sans-serif",
          fontSize: 'clamp(0.75em, 3vw, 0.85em)',
          letterSpacing: '0.02em',
          color: '#553918',
          fontWeight: 300,
          marginTop: 'clamp(4px, 1vw, 8px)',
          wordBreak: 'break-word',
          overflowWrap: 'break-word',
          maxWidth: '100%',
          padding: '0 20px',
          boxSizing: 'border-box',
          lineHeight: '1.5'
        }}>
          {loadingInitialUser ? '' : (currentUser?.bio || 'img archive')}
        </p>
        
        {/* Show unavailable message if blocked */}
        {searchedUser && user && isBlockedByThem && (
          <div style={{
            marginTop: 'clamp(12px, 3vw, 20px)',
            padding: 'clamp(12px, 3vw, 16px)',
            background: 'rgba(49,35,16,0.1)',
            borderRadius: '8px',
            textAlign: 'center'
          }}>
            <p style={{
              fontFamily: "'Montserrat', sans-serif",
              fontSize: 'clamp(12px, 3vw, 14px)',
              color: '#553918',
              letterSpacing: '0.02em',
              margin: 0
            }}>
              This account is unavailable
            </p>
          </div>
        )}

        {/* Follow Button and Menu - Only show when viewing someone else's profile and not blocked */}
        {searchedUser && user && !isBlockedByThem && (
          <div style={{
            marginTop: 'clamp(12px, 3vw, 20px)',
            display: 'flex',
            gap: '12px',
            alignItems: 'center',
            justifyContent: 'center',
            flexWrap: 'wrap'
          }}>
            <button
              onClick={handleFollowToggle}
              disabled={followLoading || isBlocked}
              style={{
                padding: 'clamp(8px, 2vw, 10px) clamp(12px, 3vw, 24px)',
                minHeight: '44px',
                minWidth: '44px',
                background: isFollowing ? '#553918' : '#312310',
                color: '#e6c57e',
                border: 'none',
                fontFamily: "'Montserrat', sans-serif",
                fontSize: 'clamp(10px, 2.5vw, 12px)',
                letterSpacing: '0.1em',
                textTransform: 'uppercase',
                cursor: (followLoading || isBlocked) ? 'not-allowed' : 'pointer',
                transition: 'all 0.4s ease',
                fontWeight: 300,
                opacity: (followLoading || isBlocked) ? 0.7 : 1,
                WebkitTapHighlightColor: 'transparent',
                borderRadius: '4px',
                whiteSpace: 'nowrap'
              }}
            >
              {followLoading ? '...' : (isFollowing ? 'Following' : 'Follow')}
            </button>
            
            {/* Menu Button */}
            <div style={{ position: 'relative' }}>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setShowMenu(!showMenu);
                }}
                style={{
                  padding: 'clamp(8px, 2vw, 10px)',
                  minHeight: '44px',
                  minWidth: '44px',
                  background: '#312310',
                  color: '#e6c57e',
                  border: 'none',
                  fontFamily: "'Montserrat', sans-serif",
                  fontSize: 'clamp(16px, 4vw, 20px)',
                  cursor: 'pointer',
                  transition: 'all 0.4s ease',
                  fontWeight: 300,
                  WebkitTapHighlightColor: 'transparent',
                  borderRadius: '4px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
                onMouseOver={(e) => {
                  e.target.style.background = '#af731f';
                  e.target.style.color = '#312310';
                }}
                onMouseOut={(e) => {
                  e.target.style.background = '#312310';
                  e.target.style.color = '#e6c57e';
                }}
              >
                ⋯
              </button>
              
              {/* Menu Dropdown */}
              {showMenu && (
                <div 
                  style={{
                    position: 'absolute',
                    top: 'calc(100% + 8px)',
                    right: 0,
                    background: 'rgba(174,180,184,0.95)',
                    backdropFilter: 'blur(20px)',
                    border: '1px solid rgba(130,85,30,0.2)',
                    boxShadow: '0 8px 30px rgba(49,35,16,0.3)',
                    borderRadius: '8px',
                    minWidth: '180px',
                    zIndex: 1001,
                    overflow: 'hidden',
                    animation: 'slideDown 0.2s ease'
                  }}
                  onClick={(e) => e.stopPropagation()}
                >
                  <button
                    onClick={() => {
                      setShowMenu(false);
                      setShowReportModal(true);
                    }}
                    style={{
                      width: '100%',
                      padding: 'clamp(12px, 3vw, 16px) clamp(16px, 4vw, 20px)',
                      fontFamily: "'Montserrat', sans-serif",
                      fontSize: 'clamp(11px, 2.5vw, 13px)',
                      letterSpacing: '0.05em',
                      color: '#312310',
                      cursor: 'pointer',
                      border: 'none',
                      background: 'none',
                      textAlign: 'left',
                      transition: 'all 0.2s ease',
                      minHeight: '44px',
                      WebkitTapHighlightColor: 'transparent',
                      borderBottom: '1px solid rgba(130,85,30,0.15)'
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.background = 'rgba(162,169,172,0.5)';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.background = 'transparent';
                    }}
                  >
                    Report User
                  </button>
                  <button
                    onClick={() => {
                      setShowMenu(false);
                      setShowBlockModal(true);
                    }}
                    style={{
                      width: '100%',
                      padding: 'clamp(12px, 3vw, 16px) clamp(16px, 4vw, 20px)',
                      fontFamily: "'Montserrat', sans-serif",
                      fontSize: 'clamp(11px, 2.5vw, 13px)',
                      letterSpacing: '0.05em',
                      color: '#312310',
                      cursor: 'pointer',
                      border: 'none',
                      background: 'none',
                      textAlign: 'left',
                      transition: 'all 0.2s ease',
                      minHeight: '44px',
                      WebkitTapHighlightColor: 'transparent'
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.background = 'rgba(162,169,172,0.5)';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.background = 'transparent';
                    }}
                  >
                    {isBlocked ? 'Unblock User' : 'Block User'}
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
        
        <div style={{
          width: '60px',
          height: '1px',
          background: '#af731f',
          margin: 'clamp(10px, 2.5vw, 20px) auto 0'
        }}></div>
        
        {/* Conditional Top Right Buttons */}
        {!searchedUser && (
          // Show My Profile when on own page
          <div className="nav-buttons-right" style={{
            position: 'absolute',
            top: 'calc(30px + env(safe-area-inset-top))',
            right: 'calc(30px + env(safe-area-inset-right))',
            display: 'flex',
            gap: 'clamp(6px, 1.5vw, 12px)',
            alignItems: 'center',
            flexWrap: 'wrap',
            justifyContent: 'flex-end',
            maxWidth: 'calc(50vw - 30px - env(safe-area-inset-right))'
          }}>
            <UserProfileButton 
              profile={profile}
              onClick={() => setShowUserProfile(!showUserProfile)}
              signOut={signOut}
              onNavigateToSettings={onNavigateToSettings}
              navigateMode={true}
            />
          </div>
        )}
      </header>

      {/* Gallery */}
      <div style={{
        maxWidth: '1600px',
        margin: '0 auto',
        padding: '0 clamp(20px, 5vw, 40px) clamp(40px, 10vw, 80px)',
        paddingBottom: 'calc(clamp(40px, 10vw, 80px) + 56px + env(safe-area-inset-bottom))',
        WebkitOverflowScrolling: 'touch'
      }}>
        {loadingInitialUser ? (
          <SkeletonGallery count={12} />
        ) : loadingPhotos && !searchedUser ? (
          <SkeletonGallery count={12} />
        ) : error && currentPhotos.length === 0 && !searchedUser ? (
          <ErrorState 
            onRetry={loadUserPhotos}
            message={getErrorMessage(error)}
          />
        ) : (
          <Gallery 
            photos={currentPhotos} 
            onDelete={handleDelete}
            canDelete={canDelete}
          />
        )}
      </div>

      {/* Bottom Navigation Bar */}
      <BottomNavBar 
        activeTab={!searchedUser ? 'profile' : null}
        onNavigateHome={onNavigateToHome}
        onSearchClick={() => setShowSearchModal(true)}
        onProfileClick={searchedUser ? clearSearch : () => {}}
        onUpload={handleUpload}
        isUploading={uploading}
        isProfileSecondaryMode={!!searchedUser}
      />

      {/* User Profile Dropdown */}
      {showUserProfile && (
        <UserProfile 
          user={user}
          profile={profile}
          onClose={() => setShowUserProfile(false)}
        />
      )}

      {/* Report User Modal */}
      {showReportModal && searchedUser && (
        <ReportUserModal
          user={searchedUser}
          onClose={() => setShowReportModal(false)}
          onReport={handleReportUser}
          currentUserId={user?.id}
        />
      )}

      {/* Block User Modal */}
      {showBlockModal && searchedUser && (
        <BlockUserModal
          user={searchedUser}
          isBlocked={isBlocked}
          onClose={() => setShowBlockModal(false)}
          onBlock={handleBlockUser}
          onUnblock={handleUnblockUser}
          currentUserId={user?.id}
        />
      )}

      {/* Close menu when clicking outside */}
      {showMenu && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            zIndex: 1000,
            background: 'transparent'
          }}
          onClick={() => setShowMenu(false)}
        />
      )}
      </div>
  );
};










